<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePortalAuthStore } from '@/stores/portalAuth'
import { identifyUser, resetAmplitude } from '@/utils/amplitude'
import AppLayout from '@/components/shared/AppLayout.vue'
import PortalLayout from '@/components/shared/PortalLayout.vue'
import Toast from '@/components/shared/Toast.vue'
import PluttoChat from '@/components/shared/PluttoChat.vue'

const route = useRoute()
const auth = useAuthStore()
const portalAuth = usePortalAuthStore()

// Use computed to avoid re-rendering App on every navigation.
// Without this, reading route.meta.public in the template makes App reactive
// to every route change, which triggers AppLayout slot re-renders that conflict
// with router-view's own component swap (breaking Teleport unmount).
const isPublicRoute = computed(() => !!route.meta.public)
const isPortalRoute = computed(() => !!route.meta.portal)
const isReportPresentation = computed(() => !!route.meta.reportPresentation)

// Amplitude: identify admin users
watch(
  () => [auth.isAuthenticated, auth.user] as const,
  ([authenticated, user]) => {
    if (authenticated && user) {
      identifyUser(user.id, {
        email: user.email,
        name: user.name,
        role: user.role,
        organization_id: user.organization_id,
        user_type: 'admin',
      })
    } else if (!authenticated) {
      resetAmplitude()
    }
  },
  { immediate: true },
)

// Amplitude: identify portal users
watch(
  () => [portalAuth.isAuthenticated, portalAuth.supplierUser] as const,
  ([authenticated, supplierUser]) => {
    if (authenticated && supplierUser) {
      identifyUser(supplierUser.id, {
        email: supplierUser.email,
        name: supplierUser.name,
        supplier_id: supplierUser.supplier_id,
        organization_id: supplierUser.organization_id,
        user_type: 'portal',
      })
    } else if (!authenticated) {
      resetAmplitude()
    }
  },
  { immediate: true },
)

// Auth check is handled by router beforeEach guard via fetchCurrentUser()
</script>

<template>
  <router-view v-if="isPublicRoute || isReportPresentation" />
  <PortalLayout v-else-if="isPortalRoute">
    <router-view />
  </PortalLayout>
  <AppLayout v-else>
    <router-view />
  </AppLayout>
  <Toast />
  <PluttoChat v-if="!isReportPresentation" />
</template>
